<template>
    <div class="grid grid-cols-12 gap-4">
        <div v-if="packStationIsActive" class="col-span-12 flex justify-start order-10">
            <button
                type="button"
                tabindex="0"
                :aria-pressed="!isPackStation"
                class="btn btn-primary rounded-none w-auto min-w-[160px]"
                :class="{ 'btn-outline': isPackStation }"
                @click.prevent="isPackStation = false; form.street = ''"
            >
                {{ $t('packStation.packStationSwitchAddress') }}
            </button>
            <button
                type="button"
                tabindex="0"
                :aria-pressed="isPackStation"
                class="btn btn-primary rounded-none w-auto min-w-[160px]"
                :class="{ 'btn-outline': !isPackStation }"
                data-cy="select-packstation"
                @click.prevent="isPackStation = true; form.street = ''"
            >
                {{ $t('packStation.packStationSwitchPackStation') }}
            </button>
        </div>

        <div v-if="isPackStation" class="form-control col-span-12 order-[15]">
            <label :for="`${id}-street`" class="label">
                <span class="label-text">{{ $t('packStation.packStationLabel') }}</span>
            </label>
            <input
                :id="`${id}-street`"
                ref="streetInput"
                v-model="form.street"
                required
                pattern="\b(?:Packstation|packstation|Postfiliale|postfiliale)\s\d+"
                :title="$t('packStation.packStationFormat')"
                type="text"
                :placeholder="$t('packStation.packStationPlaceholder')"
                class="input input-bordered w-full"
            >
        </div>

        <div v-if="isPackStation" class="form-control col-span-12 order-[20]">
            <button
                v-if="!showPackStationSearch"
                type="button"
                class="btn btn-secondary mr-auto"
                data-cy="find-packstation"
                @click.prevent="showPackStationSearch = true"
            >
                <MiscIcon name="location" size="md" />
                {{ $t('packStation.findPackStation') }}
            </button>

            <LazyCustomerAddressFormPackStation
                v-if="showPackStationSearch"
                init-modal
                @station-selected="eventListenerStationSelected"
            >
                <template #modalTrigger="triggerProps">
                    <button type="button" class="btn btn-secondary" @click.prevent="triggerProps.openModal()">
                        <MiscIcon name="location" size="md" />
                        {{ $t('packStation.findPackStation') }}
                    </button>
                </template>
            </LazyCustomerAddressFormPackStation>
        </div>

        <div v-if="isPackStation" class="form-control col-span-12 order-[30]">
            <label :for="`${id}-dhl-customer-number`" class="label">
                <span class="label-text flex items-center gap-1">
                    {{ $t('packStation.dhlCustomerNumberLabel') }}

                    <span class="tooltip cursor-pointer before:max-w-[250px]" :data-tip="$t('packStation.dhlCustomerNumberTooltip')">
                        <MiscIcon name="help-circle" size="sm" />
                    </span>
                </span>
            </label>
            <input
                :id="`${id}-dhl-customer-number`"
                ref="dhlCustomerNumberInput"
                v-model="form.additionalAddressLine1"
                required
                type="text"
                :placeholder="$t('packStation.dhlCustomerNumberPlaceholder')"
                class="input input-bordered w-full"
            >
        </div>

        <div v-if="showCompanyField" class="form-control col-span-12 order-[40]">
            <label :for="`${id}-company`" class="label">
                <span class="label-text">
                    {{ $t('customer.addressForm.company') }}
                    <span v-if="!companyRequired" class="text-base-content-subtle font-normal">({{ $t('customer.addressForm.optional') }})</span>
                </span>
            </label>
            <input
                :id="`${id}-company`"
                v-model="form.company"
                type="text"
                :required="companyRequired"
                :placeholder="companyRequired ? $t('customer.addressForm.company') : `${$t('customer.addressForm.company')} (${$t('customer.addressForm.optional')})`"
                class="input input-bordered w-full"
            >
        </div>

        <div class="form-control col-span-12 order-[45]">
            <label :for="`${id}-phoneNumber`" class="label">
                <span class="label-text">
                    {{ $t('customer.addressForm.phoneNumber') }}
                    <span class="text-base-content-subtle font-normal">({{ $t('customer.addressForm.optional') }})</span>
                </span>
            </label>
            <input
                :id="`${id}-phoneNumber`"
                v-model="form.phoneNumber"
                type="tel"
                :placeholder="`${$t('customer.addressForm.phoneNumber')} (${$t('customer.addressForm.optional')})`"
                class="input input-bordered w-full"
            >
        </div>

        <div class="form-control col-span-12 order-[50]">
            <label :for="`${id}-salutation`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.salutation') }}</span>
            </label>

            <select
                :id="`${id}-salutation`"
                v-model="form.salutationId"
                required
                class="select select-bordered w-full text-base"
            >
                <option selected disabled value="">
                    {{ $t('customer.addressForm.salutation') }}
                </option>
                <option v-for="salutation in getSalutations" :key="salutation.id" :value="salutation.id">
                    {{ salutation.translated.displayName }}
                </option>
            </select>
        </div>

        <div class="form-control col-span-12 lg:col-span-6 order-[60]">
            <label :for="`${id}-firstName`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.firstname') }}</span>
            </label>
            <input
                :id="`${id}-firstName`"
                v-model="form.firstName"
                required
                type="text"
                :placeholder="$t('customer.addressForm.firstname')"
                class="input input-bordered w-full"
            >
        </div>

        <div class="form-control col-span-12 lg:col-span-6 order-[70]">
            <label :for="`${id}-lastName`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.lastname') }}</span>
            </label>
            <input
                :id="`${id}-lastName`"
                v-model="form.lastName"
                required
                type="text"
                :placeholder="$t('customer.addressForm.lastname')"
                class="input input-bordered w-full"
            >
        </div>

        <div v-if="!isPackStation" class="form-control col-span-12 order-[80]">
            <label :for="`${id}-street`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.street') }}</span>
            </label>
            <input
                :id="`${id}-street`"
                ref="streetInput"
                v-model="form.street"
                required
                type="text"
                :placeholder="$t('customer.addressForm.street')"
                class="input input-bordered w-full"
                :class="{ 'border-4': missingStreetNumber }"
            >
            <div v-if="missingStreetNumber" class="flex justify-start gap-1 label mt-1">
                <MiscIcon name="x" size="sm" />
                <span class="label-text-alt font-bold text-xs">{{ $t('customer.addressForm.missingStreetNumber') }}</span>
            </div>
        </div>

        <div v-if="!isPackStation" class="form-control col-span-12 order-[85]">
            <label :for="`${id}-additionalAddressLine2`" class="label">
                <span class="label-text">
                    {{ $t('customer.addressForm.additionalAddressLine2') }}
                    <span class="text-base-content-subtle font-normal">({{ $t('customer.addressForm.optional') }})</span>
                </span>
            </label>
            <input
                :id="`${id}-additionalAddressLine2`"
                v-model="form.additionalAddressLine2"
                type="text"
                :placeholder="`${$t('customer.addressForm.additionalAddressLine2')} (${$t('customer.addressForm.optional')})`"
                class="input input-bordered w-full"
            >
        </div>

        <div class="form-control col-span-12 lg:col-span-4 order-[90]">
            <label :for="`${id}-zipcode`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.zipcode') }}</span>
            </label>
            <input
                :id="`${id}-zipcode`"
                v-model="form.zipcode"
                required
                :pattern="zipCodeInputPattern"
                type="text"
                :placeholder="$t('customer.addressForm.zipcode')"
                class="input input-bordered w-full number-no-spinner"
            >
        </div>

        <div class="form-control col-span-12 lg:col-span-8 order-[100]">
            <label :for="`${id}-city`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.city') }}</span>
            </label>
            <input
                :id="`${id}-city`"
                v-model="form.city"
                required
                type="text"
                :placeholder="$t('customer.addressForm.city')"
                class="input input-bordered w-full"
            >
        </div>

        <div class="form-control col-span-12 order-[110]">
            <label :for="`${id}-country`" class="label">
                <span class="label-text">{{ $t('customer.addressForm.country') }}</span>
            </label>
            <select
                :id="`${id}-country`"
                v-model="form.countryId"
                required
                class="select select-bordered w-full text-base"
            >
                <option v-for="country in computedCountries" :key="country.id" :value="country.id">
                    {{ country.name }}
                </option>
            </select>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

// Postal code validation patterns by country ISO code (from postcode-validator)
const POSTAL_CODE_PATTERNS: Record<string, RegExp> = {
    AT: /^(?!0)\d{4}$/,                          // Austria: 1234
    BE: /^\d{4}$/,                               // Belgium: 1234
    CH: /^\d{4}$/,                               // Switzerland: 1234
    CZ: /^\d{3} \d{2}$/,                         // Czech Republic: 123 45
    DE: /^\d{5}$/,                               // Germany: 12345
    DK: /^\d{4}$/,                               // Denmark: 1234
    EE: /^\d{5}$/,                               // Estonia: 12345
    ES: /^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/,      // Spain: 28001
    FI: /^\d{5}$/,                               // Finland: 12345
    FR: /^\d{2}[ ]?\d{3}$/,                      // France: 75001 or 75 001
    IT: /^\d{5}$/,                               // Italy: 12345
    LT: /^\d{5}$/,                               // Lithuania: 12345
    LU: /^\d{4}$/,                               // Luxembourg: 1234
    LV: /^\d{4}$/,                               // Latvia: 1234
    NL: /^\d{4}\s?[A-Z]{2}$/,                    // Netherlands: 1234 AB or 1234AB
    NO: /^\d{4}$/,                               // Norway: 1234
    PL: /^\d{2}-\d{3}$/,                         // Poland: 12-345
    PT: /^\d{4}-\d{3}$/,                         // Portugal: 1234-567
    SE: /^\d{3}\s?\d{2}$/,                       // Sweden: 123 45 or 12345 
    SI: /^\d{4}$/,                               // Slovenia: 1234
    SK: /^\d{3}[ ]?\d{2}$/                       // Slovakia: 12345 or 123 45
}

const props = defineProps<{
    id?: string,
    modelValue: Partial<Schemas['CustomerAddress']>,
    type?: 'shipping' | 'billing',
    companyRequired?: boolean
}>()
const emit = defineEmits(['update:modelValue', 'isPackStation'])

const form = computed({
    get () {
        return props.modelValue
    },
    set (value) {
        emit('update:modelValue', value)
    }
})
provide('customerAddressFormData', form)

const config = useRuntimeConfig().public
const { getCountries } = useCountries()
const { getSalutations } = useSalutations()
const missingStreetNumber = computed(() => {
    if (form?.value?.street == null || form?.value?.street.trim() === '') {
        return false
    }

    return !/\d/.test(form?.value?.street)
})

const { onUcIsInitialized, getUcServiceStatus, onUcConsentChange } = useUcConsentManager()
const { initPlacesLibrary, initAutocomplete, getAddressComponentByType, geocodeAddress, place } = usePlaceAutocomplete()
const streetInput: Ref<HTMLInputElement | null> = ref(null)

const showPackStationSearch = ref(false)
const packStationIsActive = computed(() => {
    return props.type === 'shipping' && config.packStationIsActive === 'true'
})
const isPackStation = ref(false)
const tmpStreet = form?.value?.street?.toLowerCase().trim()
isPackStation.value = (tmpStreet?.startsWith('packstation') || tmpStreet?.startsWith('postfiliale')) ?? false
// Strip stale additionalAddressLine1 when not in packstation mode so backend DHL validator
// (A4gPackstation/AddressSubscriber) does not trigger on key presence.
if (!isPackStation.value && form.value.additionalAddressLine1 != null) {
    form.value.additionalAddressLine1 = undefined
}
const dhlCustomerNumberInput = ref()

const computedCountries = computed(() => {
    // For shipping addresses, the saleschannel default country is selectable only
    if (props.type === 'shipping') {
        const allowedCountryIds = [config.platformDefaults.countryId]

        // Switzerland (CH) also allows Liechtenstein (LI) as shipping country
        if (config.platformDefaults.countryId === '23f1e3a2eba64c5197e47e7e5e41e785') {
            allowedCountryIds.push('d1f39aad6c554a3183d35ba7a339a963') // Add LI
        }

        return getCountries?.value?.filter((country) => {
            return allowedCountryIds.includes(country.id)
        })
    }

    return getCountries.value
})

// Show company field for business accounts or on the DE market (private customers in DE may still need it)
const showCompanyField = computed(() => {
    if (props.companyRequired) return true
    const marketCountry = getCountries.value?.find(c => c.id === config.platformDefaults.countryId)
    return marketCountry?.iso === 'DE'
})

const zipCodeInputPattern = computed(() => {
    // Get the ISO code of the selected country
    const selectedCountry = computedCountries.value?.find((country) => country.id === form.value.countryId)
    const countryIso = selectedCountry?.iso

    // If we have an ISO code and a pattern for it, use that pattern
    if (countryIso && POSTAL_CODE_PATTERNS[countryIso]) {
        // Convert RegExp to string for HTML pattern attribute
        const pattern = POSTAL_CODE_PATTERNS[countryIso].source
        return pattern
    }

    // Fallback to numeric-only pattern
    return "^[0-9]+$"
})

onMounted(async () => {
    onUcIsInitialized(handleGoogleMapsConsent)

    emit('isPackStation', isPackStation.value)
})

watch(place, async (newVal) => {
    // None is select but enter is pressed
    if (newVal == null) {
        return
    }

    const rawTypedStreet = form.value.street?.trim()

    // Get values from address components
    let street = getAddressComponentByType('route')?.long_name
    const streetNumber = getAddressComponentByType('street_number')?.long_name
    const zipcode = getAddressComponentByType('postal_code')?.long_name
    const city = getAddressComponentByType('locality')?.long_name
    const countryCode = getAddressComponentByType('country')?.short_name

    // Clone current form data, to not break reactivity
    const addressFormData = Object.assign({}, form.value)

    // Set address values
    if (streetNumber != null) {
        street = [street, streetNumber].join(' ')
    }

    if (street != null && rawTypedStreet != null && rawTypedStreet.toLowerCase() !== street.toLowerCase()) {
        if (rawTypedStreet.toLowerCase().startsWith(street.toLowerCase())) {
            // What the user typed simply extends what Google resolved (e.g. an
            // apartment suffix Google didn't structure) - safe to append as-is
            street = rawTypedStreet
        } else {
            // Typed text and Google's resolved street disagree (e.g. a spelling/
            // diacritics correction) and can't just be concatenated - re-resolve the
            // original input via the Geocoding API instead of guessing, since Place
            // Autocomplete (Legacy) does not always structure unit-level data
            // (documented Google limitation)
            const geocoded = await geocodeAddress(rawTypedStreet)
            const geocodedStreet = geocoded?.address_components?.find((component) => component.types?.includes('route'))?.long_name
            const geocodedStreetNumber = geocoded?.address_components?.find((component) => component.types?.includes('street_number'))?.long_name
            const geocodedSubpremise = geocoded?.address_components?.find((component) => component.types?.includes('subpremise'))?.long_name
            if (geocodedStreet != null && geocodedStreetNumber != null) {
                street = [geocodedStreet, geocodedStreetNumber].join(' ')
                if (geocodedSubpremise != null) {
                    street = [street, geocodedSubpremise].join('/')
                }
            }
        }
    }

    addressFormData.street = street != null ? street : ''
    addressFormData.city = city != null ? city : ''
    addressFormData.zipcode = zipcode != null ? zipcode : ''
    const matchingCountry = getCountries?.value?.find((country) => country.iso === countryCode )
    if (matchingCountry != null) {
        addressFormData.countryId = matchingCountry.id
    }

    // Write updated address to form data
    emit('update:modelValue', addressFormData)
})

watch(isPackStation, (value) => {
    // Reset additionalAddressLine1 on switch from packstation to regular address to
    // prevent error on saving regular address
    if (!value) {
        form.value.additionalAddressLine1 = undefined
    }

    emit('isPackStation', value)
})

async function bindAutoComplete (input: Ref<HTMLInputElement | null>) {
    await initPlacesLibrary()
    if (input.value != null) {
        initAutocomplete(input.value, ['address'])
    }
}

async function handleGoogleMapsConsent () {
    const ucServiceName = 'Google Maps'

    if (getUcServiceStatus(ucServiceName)) {
        await bindAutoComplete(streetInput)
    }

    onUcConsentChange(async (e) => {
        if (e.detail && e.detail.event == 'consent_status') {
            if (e.detail[ucServiceName] === true && !window.google?.maps?.places) {
                await bindAutoComplete(streetInput)
            } else if (e.detail[ucServiceName] === false && window.google?.maps?.places) {
                // Reload page to make sure google script is not loaded anymore
                location.reload()
            }
        }
    })
}

function eventListenerStationSelected () {
    if (dhlCustomerNumberInput?.value != null) {
        dhlCustomerNumberInput?.value.focus()
    }
}
</script>
